import { selectors as pullRequestsSelectors } from 'reducers/pullRequests';
import { postedCommentSnippets, postedComments } from '../selectors';
import {
  RouterRootState,
  createMatchSelector,
  matchSelectorFn,
} from 'connected-react-router';
import { createSelector } from 'reselect';
import { History } from 'history';

export const reviewMatchSelector: matchSelectorFn<
  RouterRootState<History.UnknownFacade>,
  {
    projectName: string;
    organizationName: string;
    providerId: string;
    reviewNumber: string;
  }
> = createMatchSelector(
  `/dash/projects/:providerId([1-9])/:organizationName/:projectName([^/]+)/reviews/:reviewNumber/`
);

export const reviewMatchParams = (state: any) => {
  const match = reviewMatchSelector(state);
  return match?.params;
};

export const getInlineComments = (state: any) => {
  const reviewParams = reviewMatchParams(state);
  if (!reviewParams) {
    return [];
  }
  const { providerId, organizationName, projectName, reviewNumber } =
    reviewParams;

  return pullRequestsSelectors.pullRequestCommentsForOrgAndRepo(
    state,
    providerId,
    organizationName,
    projectName,
    reviewNumber
  );
};

export const getInlineCommentSnippets = (state: any) => {
  const reviewParams = reviewMatchParams(state);
  if (!reviewParams) {
    return [];
  }
  const { providerId, organizationName, projectName, reviewNumber } =
    reviewParams;

  return pullRequestsSelectors.pullRequestCommentSnippetsForOrgAndRepo(
    state,
    providerId,
    organizationName,
    projectName,
    reviewNumber
  );
};

export const getInlineCommentSnippetsByUUID = createSelector(
  getInlineCommentSnippets,
  (snippets) =>
    snippets?.reduce(
      (
        obj: any,
        commentSnippet: { comment_uuid: string; snippet: string }
      ) => ({
        ...obj,
        [commentSnippet.comment_uuid]: commentSnippet.snippet,
      }),
      {}
    ) ?? {}
);

export const getInlineCommentsAndSnippets = createSelector(
  [getInlineComments, getInlineCommentSnippetsByUUID],
  (comments, snippetsByUUID) =>
    comments?.length
      ? comments.map((comment: any) => ({
          ...comment,
          snippet: snippetsByUUID[comment.uuid],
        }))
      : []
);

export const getPostedComments = (state: any) => {
  const reviewParams = reviewMatchParams(state);
  if (!reviewParams) {
    return [];
  }

  const {
    providerId,
    organizationName: organizationSlug,
    projectName,
    reviewNumber,
  } = reviewParams;

  return postedComments(state, {
    providerId: Number(providerId),
    organizationSlug,
    projectName,
    reviewNumber: Number(reviewNumber),
  });
};

export const getPostedCommentSnippets = (state: any) => {
  const reviewParams = reviewMatchParams(state);
  if (!reviewParams) {
    return [];
  }

  const {
    providerId,
    organizationName: organizationSlug,
    projectName,
    reviewNumber,
  } = reviewParams;

  return postedCommentSnippets(state, {
    providerId: Number(providerId),
    organizationSlug,
    projectName,
    reviewNumber: Number(reviewNumber),
  });
};

export const getPostedCommentSnippetsByUUID = createSelector(
  getPostedCommentSnippets,
  (snippets) =>
    snippets?.reduce(
      (
        obj: any,
        commentSnippet: { comment_uuid: string; snippet: string }
      ) => ({
        ...obj,
        [commentSnippet.comment_uuid]: commentSnippet.snippet,
      }),
      {}
    ) ?? {}
);

export const getPostedCommentsAndSnippets = createSelector(
  [getPostedComments, getPostedCommentSnippetsByUUID],
  (comments, snippetsByUUID) =>
    comments?.length
      ? comments.map((comment: any) => ({
          ...comment,
          snippet: snippetsByUUID[comment.uuid],
        }))
      : []
);

export const inline = createSelector(
  [getInlineCommentsAndSnippets, getPostedCommentsAndSnippets],
  (inline, posted) => [...inline, ...posted]
);

export const selectors = {
  reviewMatchSelector,
  reviewMatchParams,
  inlineComments: getInlineComments,
  inlineCommentSnippets: getInlineCommentSnippets,
  inlineCommentSnippetsByUUID: getInlineCommentSnippetsByUUID,
  inlineCommentsAndSnippets: getInlineCommentsAndSnippets,
  postedComments: getPostedComments,
  postedCommentSnippets: getPostedCommentSnippets,
  postedCommentSnippetsByUUID: getPostedCommentSnippetsByUUID,
  postedCommentsAndSnippets: getPostedCommentsAndSnippets,
  inline,
};
